import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  activeTodoMentionQuery,
  driftDeepLinkForWave,
  isWaveTodoAgentAssignee,
  listTodoMentionCandidates,
  parseTodoAssigneesFromTitle,
  todoItemCommentAnchor,
  WaveBlockKind,
  type WaveBlock,
  type WaveCommentRecord,
  type WaveDocEditor,
  type WaveParticipantRecord,
  type WaveTodoItem,
  type WaveTodoMentionCandidate,
} from "@vida/wave-core";
import { WaveAuthorAvatar } from "../presence/WaveAuthorAvatar";
import { WaveCommentThread } from "../editor/WaveCommentThread";
import { inferWaveTodoTitle } from "../../services/waveTodoInference";
import { renderTodoTitleInline } from "../../services/waveTodoTitle";
import { WaveTodoMentionPicker } from "./WaveTodoMentionPicker";

type EditHandlers = {
  onFocus?: () => void;
  onBlur?: () => void;
  onTypingActivity?: () => void;
};

type Props = {
  waveId: string;
  spaceId: string;
  block: WaveBlock;
  editor?: WaveDocEditor | null;
  currentUserSub: string;
  participants: WaveParticipantRecord[];
  mentionParticipants?: WaveParticipantRecord[];
  comments: WaveCommentRecord[];
  readOnly?: boolean;
  remoteContentRevision?: number;
  hideHeaderMenu?: boolean;
  hideDone?: boolean;
  focusItemId?: string | null;
  onFocusHandled?: () => void;
  edit: EditHandlers;
  onPostComment?: (anchor: string, body: string, parentId: string | null) => void;
  onToggleReaction?: (commentId: string, emoji: string) => void;
  onAfterEdit?: () => void;
};

function assigneesEqual(a: string[], b: string[]): boolean {
  if (a.length !== b.length) return false;
  const sa = [...a].sort();
  const sb = [...b].sort();
  return sa.every((v, i) => v === sb[i]);
}

export function WaveTodoListBlock({
  waveId,
  spaceId,
  block,
  editor,
  currentUserSub,
  participants,
  mentionParticipants,
  comments,
  readOnly,
  remoteContentRevision = 0,
  hideHeaderMenu = false,
  hideDone: hideDoneProp,
  focusItemId: focusItemIdProp,
  onFocusHandled,
  edit,
  onPostComment,
  onToggleReaction,
  onAfterEdit,
}: Props) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [internalHideDone, setInternalHideDone] = useState(false);
  const [openCommentsItemId, setOpenCommentsItemId] = useState<string | null>(null);
  const [internalFocusItemId, setInternalFocusItemId] = useState<string | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const hideDone = hideHeaderMenu ? (hideDoneProp ?? false) : internalHideDone;
  const focusItemId = hideHeaderMenu ? (focusItemIdProp ?? null) : internalFocusItemId;

  const items = block.payload.todoItems ?? [];
  const visibleItems = hideDone ? items.filter((i) => !i.done) : items;

  useEffect(() => {
    if (!menuOpen || hideHeaderMenu) return;
    const onDoc = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [hideHeaderMenu, menuOpen]);

  const commentCountByItem = useMemo(() => {
    const counts = new Map<string, number>();
    for (const c of comments) {
      const parsed = c.blockAnchorId.startsWith(`${block.id}#`)
        ? c.blockAnchorId.slice(block.id.length + 1)
        : null;
      if (!parsed) continue;
      counts.set(parsed, (counts.get(parsed) ?? 0) + 1);
    }
    return counts;
  }, [block.id, comments]);

  const roster = mentionParticipants ?? participants;

  const addItem = useCallback(
    (focusNew = false) => {
      if (!editor || readOnly) return;
      edit.onTypingActivity?.();
      const created = editor.addTodoItem(block.id, currentUserSub);
      if (focusNew && !hideHeaderMenu) setInternalFocusItemId(created.id);
      onAfterEdit?.();
    },
    [block.id, currentUserSub, edit, editor, hideHeaderMenu, onAfterEdit, readOnly],
  );

  const openInDrift = useCallback(() => {
    setMenuOpen(false);
    const url = driftDeepLinkForWave(waveId);
    window.open(url, "_blank", "noopener,noreferrer");
  }, [waveId]);

  const handleAutoFocused = useCallback(() => {
    if (hideHeaderMenu) {
      onFocusHandled?.();
      return;
    }
    if (focusItemId) setInternalFocusItemId(null);
  }, [focusItemId, hideHeaderMenu, onFocusHandled]);

  return (
    <div className="wave-todo-list-block">
      <div className="wave-todo-list-header">
        <input
          className="wave-title wave-todo-list-title"
          value={block.payload.title ?? "Todo"}
          readOnly={readOnly}
          placeholder="Lista todo"
          onFocus={edit.onFocus}
          onBlur={edit.onBlur}
          onChange={(e) => {
            edit.onTypingActivity?.();
            editor?.setText(block.id, "title", e.target.value);
            onAfterEdit?.();
          }}
        />
        {!hideHeaderMenu && (
          <div className="wave-todo-list-menu-wrap" ref={menuRef}>
            <button
              type="button"
              className="wave-todo-list-menu-btn"
              aria-label="Menu lista todo"
              onClick={() => setMenuOpen((v) => !v)}
            >
              ⋯
            </button>
            {menuOpen && (
              <div className="wave-todo-list-menu" role="menu">
                <button type="button" role="menuitem" onClick={() => addItem(true)} disabled={readOnly}>
                  Aggiungi todo
                </button>
                <button type="button" role="menuitem" onClick={() => setInternalHideDone((v) => !v)}>
                  {hideDone ? "Mostra completati" : "Comprimi completati"}
                </button>
                <button type="button" role="menuitem" onClick={openInDrift}>
                  Apri in Drift
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      <ul className="wave-todo-list-items" role="list">
        {visibleItems.length === 0 && (
          <li className="wave-todo-list-empty muted">Nessun todo — aggiungine uno.</li>
        )}
        {visibleItems.map((item) => (
          <TodoListItemRow
            key={item.id}
            waveId={waveId}
            blockId={block.id}
            item={item}
            editor={editor}
            currentUserSub={currentUserSub}
            participants={roster}
            mentionRoster={roster}
            comments={comments}
            commentCount={commentCountByItem.get(item.id) ?? 0}
            commentsOpen={openCommentsItemId === item.id}
            readOnly={readOnly}
            remoteContentRevision={remoteContentRevision}
            autoFocus={focusItemId === item.id}
            onAutoFocused={handleAutoFocused}
            edit={edit}
            onToggleComments={() =>
              setOpenCommentsItemId((cur) => (cur === item.id ? null : item.id))
            }
            onPostComment={onPostComment}
            onToggleReaction={onToggleReaction}
            onAfterEdit={onAfterEdit}
            onEnterCreateNext={() => addItem(true)}
          />
        ))}
      </ul>

      {!readOnly && (
        <button type="button" className="link-btn wave-todo-list-add" onClick={() => addItem(true)}>
          + Aggiungi todo
        </button>
      )}

      {block.payload.widgetIndex != null && (
        <p className="wave-todo-list-meta muted">
          Widget #{block.payload.widgetIndex}
          {spaceId ? ` · spazio ${spaceId.slice(0, 8)}…` : ""}
        </p>
      )}
    </div>
  );
}

function TodoListItemRow({
  waveId,
  blockId,
  item,
  editor,
  currentUserSub,
  participants,
  mentionRoster,
  comments,
  commentCount,
  commentsOpen,
  readOnly,
  remoteContentRevision,
  autoFocus,
  onAutoFocused,
  edit,
  onToggleComments,
  onPostComment,
  onToggleReaction,
  onAfterEdit,
  onEnterCreateNext,
}: {
  waveId: string;
  blockId: string;
  item: WaveTodoItem;
  editor?: WaveDocEditor | null;
  currentUserSub: string;
  participants: WaveParticipantRecord[];
  mentionRoster: WaveParticipantRecord[];
  comments: WaveCommentRecord[];
  commentCount: number;
  commentsOpen: boolean;
  readOnly?: boolean;
  remoteContentRevision?: number;
  autoFocus?: boolean;
  onAutoFocused?: () => void;
  edit: EditHandlers;
  onToggleComments?: () => void;
  onPostComment?: (anchor: string, body: string, parentId: string | null) => void;
  onToggleReaction?: (commentId: string, emoji: string) => void;
  onAfterEdit?: () => void;
  onEnterCreateNext?: () => void;
}) {
  const [draft, setDraft] = useState(item.title);
  const [editing, setEditing] = useState(false);
  const [inferring, setInferring] = useState(false);
  const [mentionStart, setMentionStart] = useState<number | null>(null);
  const [mentionQuery, setMentionQuery] = useState("");
  const [mentionIndex, setMentionIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const mentionPickerRef = useRef<HTMLDivElement>(null);
  const skipBlurRef = useRef(false);
  const titlePushTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const anchor = todoItemCommentAnchor(blockId, item.id);

  const mentionOpen = mentionStart != null;
  const mentionCandidates = useMemo(
    () => listTodoMentionCandidates(mentionRoster, mentionQuery),
    [mentionRoster, mentionQuery],
  );

  useEffect(() => {
    if (!editing) setDraft(item.title);
  }, [item.title, remoteContentRevision, editing]);

  useEffect(() => {
    if (!autoFocus) return;
    setEditing(true);
    requestAnimationFrame(() => {
      inputRef.current?.focus();
      onAutoFocused?.();
    });
  }, [autoFocus, onAutoFocused]);

  const syncAssigneesFromTitle = useCallback(
    (title: string) => {
      if (readOnly || !editor) return;
      const assignees = parseTodoAssigneesFromTitle(title, mentionRoster);
      if (!assigneesEqual(assignees, item.assignees)) {
        editor.setTodoItemAssignees(blockId, item.id, assignees);
        onAfterEdit?.();
      }
    },
    [blockId, editor, item.assignees, item.id, mentionRoster, onAfterEdit, readOnly],
  );

  const pushTitleNow = useCallback(
    (value: string, syncAssignees = false) => {
      if (readOnly || !editor) return;
      if (value !== item.title) {
        editor.setTodoItemTitle(blockId, item.id, value);
        onAfterEdit?.();
      }
      if (syncAssignees) syncAssigneesFromTitle(value);
    },
    [blockId, editor, item.id, item.title, onAfterEdit, readOnly, syncAssigneesFromTitle],
  );

  const scheduleTitlePush = useCallback(
    (value: string) => {
      if (titlePushTimerRef.current) clearTimeout(titlePushTimerRef.current);
      titlePushTimerRef.current = setTimeout(() => {
        titlePushTimerRef.current = null;
        pushTitleNow(value, false);
      }, 400);
    },
    [pushTitleNow],
  );

  useEffect(
    () => () => {
      if (titlePushTimerRef.current) clearTimeout(titlePushTimerRef.current);
    },
    [],
  );

  const commitTitle = useCallback(
    (value: string) => {
      if (titlePushTimerRef.current) {
        clearTimeout(titlePushTimerRef.current);
        titlePushTimerRef.current = null;
      }
      pushTitleNow(value, true);
    },
    [pushTitleNow],
  );

  const updateMentionState = useCallback((text: string, cursor: number) => {
    const active = activeTodoMentionQuery(text, cursor);
    if (active) {
      setMentionStart(active.start);
      setMentionQuery(active.query);
      setMentionIndex(0);
    } else {
      setMentionStart(null);
      setMentionQuery("");
      setMentionIndex(0);
    }
  }, []);

  const applyMention = useCallback(
    (candidate: WaveTodoMentionCandidate) => {
      const input = inputRef.current;
      const cursor = input?.selectionStart ?? draft.length;
      const before = draft.slice(0, mentionStart ?? cursor);
      const after = draft.slice(cursor);
      const insert = `@${candidate.label} `;
      const next = before + insert + after;
      setDraft(next);
      commitTitle(next);
      setMentionStart(null);
      setMentionQuery("");
      setMentionIndex(0);
      requestAnimationFrame(() => {
        const pos = before.length + insert.length;
        input?.setSelectionRange(pos, pos);
        input?.focus();
      });
    },
    [commitTitle, draft, mentionStart],
  );

  const toggleDone = () => {
    if (readOnly) return;
    edit.onTypingActivity?.();
    editor?.toggleTodoItemDone(blockId, item.id, !item.done);
    onAfterEdit?.();
  };

  const runInference = async (value: string) => {
    if (readOnly || !value.trim()) return;
    if (value.includes("**") || /^[\p{Extended_Pictographic}]/u.test(value.trim())) {
      return;
    }
    setInferring(true);
    try {
      const inferred = await inferWaveTodoTitle(waveId, value);
      if (inferred?.title) {
        setDraft(inferred.title);
        commitTitle(inferred.title);
        if (inferred.dueDateISO) {
          editor?.setTodoItemDueDate(blockId, item.id, inferred.dueDateISO);
        }
        if (inferred.priority && inferred.priority !== "none") {
          editor?.setTodoItemPriority(blockId, item.id, inferred.priority);
        }
        onAfterEdit?.();
      }
    } catch {
      /* keep raw title */
    } finally {
      setInferring(false);
    }
  };

  const finishEditing = async () => {
    edit.onBlur?.();
    setEditing(false);
    setMentionStart(null);
    if (readOnly || !draft.trim()) return;
    commitTitle(draft);
    await runInference(draft);
  };

  const handleBlur = () => {
    if (skipBlurRef.current) {
      skipBlurRef.current = false;
      return;
    }
    window.setTimeout(() => {
      const active = document.activeElement;
      if (mentionPickerRef.current?.contains(active)) return;
      if (inputRef.current === active) return;
      void finishEditing();
    }, 0);
  };

  const handleEnterKey = () => {
    if (mentionOpen && mentionCandidates.length > 0) {
      applyMention(mentionCandidates[Math.min(mentionIndex, mentionCandidates.length - 1)]!);
      return;
    }
    skipBlurRef.current = true;
    commitTitle(draft);
    void runInference(draft);
    setMentionStart(null);
    setEditing(true);
    onEnterCreateNext?.();
  };

  return (
    <li className={`wave-todo-item${item.done ? " done" : ""}`}>
      <div className="wave-todo-item-row">
        <button
          type="button"
          className="task-check"
          disabled={readOnly}
          aria-label={item.done ? "Segna da fare" : "Completa"}
          onClick={toggleDone}
        >
          {item.done ? "☑" : "☐"}
        </button>

        <div className="wave-todo-item-editor-wrap">
          {editing || !draft ? (
            <>
              <input
                ref={inputRef}
                type="text"
                className="wave-title wave-todo-item-input"
                value={draft}
                readOnly={readOnly || inferring}
                autoComplete="off"
                autoCorrect="off"
                spellCheck={false}
                placeholder="Descrizione todo — usa @ per assegnare"
                onFocus={() => {
                  setEditing(true);
                  edit.onFocus?.();
                }}
                onBlur={handleBlur}
                onChange={(e) => {
                  setEditing(true);
                  edit.onTypingActivity?.();
                  const value = e.target.value;
                  const cursor = e.target.selectionStart ?? value.length;
                  setDraft(value);
                  updateMentionState(value, cursor);
                  scheduleTitlePush(value);
                }}
                onKeyDown={(e) => {
                  if (e.altKey || e.metaKey) {
                    e.stopPropagation();
                  }
                  if (mentionOpen) {
                    if (e.key === "ArrowDown") {
                      e.preventDefault();
                      setMentionIndex((i) => Math.min(i + 1, Math.max(mentionCandidates.length - 1, 0)));
                      return;
                    }
                    if (e.key === "ArrowUp") {
                      e.preventDefault();
                      setMentionIndex((i) => Math.max(i - 1, 0));
                      return;
                    }
                    if (e.key === "Escape") {
                      e.preventDefault();
                      setMentionStart(null);
                      return;
                    }
                  }
                  if (e.key === "Enter") {
                    e.preventDefault();
                    handleEnterKey();
                  }
                }}
              />
              {mentionOpen && !readOnly && (
                <div ref={mentionPickerRef}>
                  <WaveTodoMentionPicker
                    candidates={mentionCandidates}
                    selectedIndex={mentionIndex}
                    participants={mentionRoster}
                    onSelect={applyMention}
                    onHover={setMentionIndex}
                  />
                </div>
              )}
            </>
          ) : (
            <button
              type="button"
              className="wave-todo-item-title-display"
              disabled={readOnly}
              onClick={() => setEditing(true)}
            >
              {renderTodoTitleInline(draft)}
            </button>
          )}
        </div>

        <div className="wave-todo-item-actions">
          <button
            type="button"
            className={`link-btn wave-todo-comment-btn${commentCount > 0 ? " has-comments" : ""}`}
            onClick={onToggleComments}
            aria-label="Commenti todo"
          >
            💬{commentCount > 0 ? ` ${commentCount}` : ""}
          </button>

          {item.assignees.length > 0 && (
            <div className="wave-todo-assignees">
              {item.assignees.slice(0, 3).map((ref) =>
                isWaveTodoAgentAssignee(ref) ? (
                  <span key={ref} className="wave-todo-agent-chip" title={ref}>
                    🤖
                  </span>
                ) : (
                  <WaveAuthorAvatar
                    key={ref}
                    authorSub={ref}
                    participants={participants}
                    size={22}
                  />
                ),
              )}
            </div>
          )}
        </div>
      </div>

      {commentsOpen && onPostComment && (
        <div className="wave-todo-item-comments">
          <WaveCommentThread
            comments={comments}
            blockAnchorId={anchor}
            participants={participants}
            mentionParticipants={mentionRoster}
            currentUserSub={currentUserSub}
            readOnly={readOnly}
            onPost={(body, parentId) => onPostComment(anchor, body, parentId)}
            onToggleReaction={
              onToggleReaction
                ? (commentId, emoji) => onToggleReaction(commentId, emoji)
                : () => {}
            }
            onCollapse={onToggleComments}
          />
        </div>
      )}
    </li>
  );
}

export function isWaveTodoListBlock(block: WaveBlock): boolean {
  return block.kind === WaveBlockKind.TodoList;
}
